/**
 * Utilidades y helpers para plantillas cíclicas (normalización, migración v3→v4).
 * El listado y el guardado principal están en la API (`plantillaCiclicaService`).
 * `loadPlantillasCiclicos` / `savePlantillasCiclicos` siguen en localStorage solo para
 * importación única a servidor o usos puntuales.
 *
 * Estructura: `bloques` = por cada tiempo, lista de tipos de plato.
 */

/** v4: sin datos demo; migración desde v3 / v2 / v1 */
export const STORAGE_PLANTILLAS_CICLICOS = 'pc_plantillas_ciclicos_v4';
const STORAGE_PLANTILLAS_V3 = 'pc_plantillas_ciclicos_v3';

const DIAS_KEYS = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'];

/** Plantillas de ejemplo retiradas (v4 con demo); se eliminan al leer. */
const PLANTILLAS_DEMO_RETIRADAS = new Set([
  'pc-demo-cocina-principal-lv',
  'pc-demo-cocina-principal-finsem',
]);

function filtrarDemosRetirados(list) {
  const a = list || [];
  return a.filter((p) => !PLANTILLAS_DEMO_RETIRADAS.has(p.id));
}

function emptyDiasSemana() {
  return {
    lunes: false,
    martes: false,
    miercoles: false,
    jueves: false,
    viernes: false,
    sabado: false,
    domingo: false,
  };
}

function migrateDiasPorSemanaANumero(n) {
  const d = emptyDiasSemana();
  const c = Math.min(7, Math.max(0, parseInt(n, 10) || 0));
  DIAS_KEYS.forEach((k, i) => {
    d[k] = i < c;
  });
  return d;
}

function lineasDesdeTiemposConfig(tc) {
  const lineas = [];
  if (!tc || typeof tc !== 'object') return lineas;
  Object.entries(tc).forEach(([tiempoId, tipoIds]) => {
    (Array.isArray(tipoIds) ? tipoIds : []).forEach((tipoPlatoId) => {
      lineas.push({ tiempoId, tipoPlatoId });
    });
  });
  return lineas;
}

/** Agrupa líneas por tiempo conservando el orden de aparición de cada tipo. */
export function lineasToBloques(lineas) {
  const bloquesOrder = [];
  const tiempoToOrden = new Map();
  (lineas || []).forEach((l) => {
    if (!l.tiempoId || !l.tipoPlatoId) return;
    if (!tiempoToOrden.has(l.tiempoId)) {
      tiempoToOrden.set(l.tiempoId, []);
      bloquesOrder.push(l.tiempoId);
    }
    const arr = tiempoToOrden.get(l.tiempoId);
    if (!arr.includes(l.tipoPlatoId)) arr.push(l.tipoPlatoId);
  });
  return bloquesOrder.map((tiempoId) => ({
    id: `blk-${tiempoId}-${Math.random().toString(36).slice(2, 7)}`,
    tiempoId,
    ordenTipos: tiempoToOrden.get(tiempoId) || [],
  }));
}

export function bloquesToLineas(bloques) {
  const lineas = [];
  if (!Array.isArray(bloques)) return lineas;
  bloques.forEach((b) => {
    const orden = Array.isArray(b.ordenTipos)
      ? b.ordenTipos
      : Array.isArray(b.tipoPlatoIds)
        ? b.tipoPlatoIds
        : [];
    orden.forEach((tipoPlatoId) => {
      lineas.push({
        id: `ln-${b.tiempoId}-${tipoPlatoId}-${Math.random().toString(36).slice(2, 5)}`,
        tiempoId: b.tiempoId,
        tipoPlatoId,
      });
    });
  });
  return lineas;
}

export function loadPlantillasCiclicos() {
  try {
    let raw = localStorage.getItem(STORAGE_PLANTILLAS_CICLICOS);
    if (!raw) {
      const v3 = localStorage.getItem(STORAGE_PLANTILLAS_V3);
      if (v3) {
        try {
          const parsed = JSON.parse(v3);
          if (Array.isArray(parsed)) {
            const migrated = filtrarDemosRetirados(parsed.map(normalizePlantilla));
            if (migrated.length > 0) {
              localStorage.setItem(STORAGE_PLANTILLAS_CICLICOS, JSON.stringify(migrated));
              return migrated;
            }
            localStorage.setItem(STORAGE_PLANTILLAS_CICLICOS, '[]');
            return [];
          }
        } catch {
          /* ignore */
        }
      }
      for (const legacyKey of ['pc_plantillas_ciclicos_v2', 'pc_plantillas_ciclicos_v1']) {
        const legacy = localStorage.getItem(legacyKey);
        if (legacy) {
          try {
            const parsed = JSON.parse(legacy);
            if (Array.isArray(parsed)) {
              const migrated = filtrarDemosRetirados(parsed.map(normalizePlantilla));
              if (migrated.length > 0) {
                localStorage.setItem(STORAGE_PLANTILLAS_CICLICOS, JSON.stringify(migrated));
                return migrated;
              }
              break;
            }
          } catch {
            /* ignore */
          }
        }
      }
      localStorage.setItem(STORAGE_PLANTILLAS_CICLICOS, '[]');
      return [];
    }
    const p = JSON.parse(raw);
    const list = Array.isArray(p) ? p.map(normalizePlantilla) : [];
    const sinDemos = filtrarDemosRetirados(list);
    if (sinDemos.length !== list.length) {
      try {
        localStorage.setItem(STORAGE_PLANTILLAS_CICLICOS, JSON.stringify(sinDemos));
      } catch {
        /* ignore */
      }
    }
    return sinDemos;
  } catch {
    try {
      localStorage.setItem(STORAGE_PLANTILLAS_CICLICOS, '[]');
    } catch {
      /* ignore */
    }
    return [];
  }
}

export function savePlantillasCiclicos(list) {
  localStorage.setItem(STORAGE_PLANTILLAS_CICLICOS, JSON.stringify(list));
}

export function normalizePlantilla(raw) {
  const id = raw.id || `pc-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  const nombre = String(raw.nombre || '').trim() || 'Sin nombre';
  const proyectoId = raw.proyectoId != null ? String(raw.proyectoId) : '';

  let diasSemana = emptyDiasSemana();
  if (raw.diasSemana && typeof raw.diasSemana === 'object') {
    DIAS_KEYS.forEach((k) => {
      diasSemana[k] = !!raw.diasSemana[k];
    });
  } else if (raw.diasPorSemana != null) {
    diasSemana = migrateDiasPorSemanaANumero(raw.diasPorSemana);
  } else {
    diasSemana = migrateDiasPorSemanaANumero(5);
  }

  let bloques = [];
  if (Array.isArray(raw.bloques) && raw.bloques.length > 0) {
    bloques = raw.bloques
      .map((b, i) => {
        const ordenTipos = Array.isArray(b.ordenTipos)
          ? b.ordenTipos.filter(Boolean)
          : Array.isArray(b.tipoPlatoIds)
            ? b.tipoPlatoIds.filter(Boolean)
            : [];
        return {
          id: b.id || `blk-${i}-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`,
          tiempoId: b.tiempoId != null ? String(b.tiempoId) : '',
          ordenTipos,
        };
      })
      .filter((b) => b.tiempoId);
  } else {
    let lineas = [];
    if (Array.isArray(raw.lineas) && raw.lineas.length > 0) {
      lineas = raw.lineas
        .map((l) => ({
          tiempoId: l.tiempoId != null ? String(l.tiempoId) : '',
          tipoPlatoId: l.tipoPlatoId != null ? String(l.tipoPlatoId) : '',
        }))
        .filter((l) => l.tiempoId && l.tipoPlatoId);
    } else if (raw.tiemposConfig) {
      lineas = lineasDesdeTiemposConfig(raw.tiemposConfig);
    }
    bloques = lineasToBloques(lineas);
  }

  const lineas = bloquesToLineas(bloques);

  return {
    id,
    nombre,
    proyectoId,
    diasSemana,
    bloques,
    lineas,
  };
}

export function lineasATiemposConfig(lineas) {
  const tc = {};
  (lineas || []).forEach((l) => {
    if (!l.tiempoId || !l.tipoPlatoId) return;
    if (!tc[l.tiempoId]) tc[l.tiempoId] = [];
    if (!tc[l.tiempoId].includes(l.tipoPlatoId)) tc[l.tiempoId].push(l.tipoPlatoId);
  });
  return tc;
}
